@file:OptIn(ExperimentalSerializationApi::class)

package apps

import api.AppType
import api.ResourceType
import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.Serializable
import kotlinx.serialization.protobuf.ProtoNumber
import ksp.resource.*
import org.ssoft.common.tools.nowMilliseconds
import org.ssoft.common.type.Mask
import tk.mallumo.utils.GenId
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds

@Serializable
sealed interface App {

    @ProtoNumber(1)
    val appId: Long

    /** @see api.AppType */
    @ProtoNumber(2)
    val type: Int

    companion object {
        fun empty(appId: Long, type: AppType): App = when (type) {
            AppType.RSS -> Rss(appId, type.type)
            AppType.IMAGE -> Image(appId, type.type)
            AppType.NONE -> None(appId, type.type)
            AppType.PDF -> Pdf(appId, type.type)
            AppType.VIDEO -> Video(appId, type.type)
            AppType.WWW_EMBEDDED -> WwwEmbedded(appId, type.type)
            AppType.WWW_LINK -> WwwLink(appId, type.type)
            AppType.APP_GROUP -> AppGroup(appId, type.type)
            AppType.BALOON -> Balloon(appId, type.type)
            AppType.QUIZ -> Quiz(appId, type.type)
            AppType.GALLERY -> Gallery(appId, type.type)
            AppType.WHEEL -> Wheel(appId, type.type)
            AppType.CAMERA -> Camera(appId, type.type)
            AppType.VIDEO_ALPHA_LOOP -> VideoAlphaLoop(appId, type.type)
            AppType.MAZE -> Maze(appId, type.type)
            AppType.USB_PLAYER -> UsbPlayer(appId, type.type)
            AppType.KINDERGARTEN -> Kindergarten(appId, type.type)
        }
    }

    @Serializable
    data class None(
        @ProtoNumber(1)
        override val appId: Long = -1,
        @ProtoNumber(2)
        override val type: Int = AppType.NONE.type
    ) : App


    @Serializable
    data class Quiz(
        @ProtoNumber(1)
        override val appId: Long,
        @ProtoNumber(2)
        override val type: Int = AppType.QUIZ.type,
        @ProtoNumber(3)
        @ResClass(Content::class, false, false)
        var content: Content = Content()
    ) : App {

        @Serializable
        data class Content(
            @ProtoNumber(1)
            @ResImage(false)
            var backgroundImage: Long = -21L,
            @ProtoNumber(2)
            var questionsStack: QuestionStack = QuestionStack(),
            @ProtoNumber(3)
            var translations: Translations = Translations()
        )

        @Serializable
        data class Translations(
            /**
             * autofill
             */
            @ProtoNumber(1)
            var tooltipAnswerSingle: String = "",
            /**
             * autofill
             */
            @ProtoNumber(2)
            var tooltipAnswerMulti: String = "",
            /**
             * autofill
             */
            @ProtoNumber(3)
            var tooltipAnswerText: String = "",
            /**
             * autofill
             */
            @ProtoNumber(4)
            var buttonNext: String = "",
            /**
             * autofill
             */
            @ProtoNumber(5)
            var buttonSend: String = "",
            /**
             * autofill + manual
             */
            @ProtoNumber(6)
            var resultTitle: String = "",
            /**
             * autofill + manual
             */
            @ProtoNumber(7)
            var resultMsg: String = "",
            /**
             * autofill
             */
            @ProtoNumber(8)
            var labelQuestionOf: String = "",
        )


        @Serializable
        data class QuestionStack(
            @ProtoNumber(1)
            var revision: Int = 0,
            @ProtoNumber(2)
            var questions: MutableList<Question> = mutableListOf(),
        )

        @Serializable
        data class Question(
            @ProtoNumber(1)
            var position: Int = 0,
            @ProtoNumber(2)
            var key: Long = kotlin.time.Clock.nowMilliseconds(),
            /**
             * @see Type
             */
            @ProtoNumber(3)
            var type: Int = Type.SINGLE.type,
            @ProtoNumber(4)
            var value: String = "",
            @ProtoNumber(5)
            val answers: MutableList<Answer> = mutableListOf()
        ) {
            sealed class Type(val type: Int) {
                data object SINGLE : Type(2)
                data object MULTI : Type(4)
                data object TEXT : Type(8)

                infix fun match(type: Int): Boolean = this.type == type

                companion object {

                    val all by lazy {
                        listOf(SINGLE, MULTI, TEXT)
                    }

                    operator fun get(type: Int): Type {
                        return all.firstOrNull { it match type } ?: TEXT
                    }
                }
            }
        }

        @Serializable
        data class Answer(
            @ProtoNumber(1)
            var key: Long = kotlin.time.Clock.nowMilliseconds(),
            @ProtoNumber(2)
            var value: String = ""
        )
    }

    @Serializable
    data class Balloon(
        @ProtoNumber(1)
        override val appId: Long,
        @ProtoNumber(2)
        override val type: Int = AppType.BALOON.type,
        @ProtoNumber(3)
        var content: Content = Content()
    ) : App {
        @Serializable
        data class Content(
            @ProtoNumber(1)
            var levelDuration: Int = 1,
            @ProtoNumber(2)
            var levelSpawn: Int = 1,
            @ProtoNumber(3)
            var sizePct: Float = 0.2F,
            @ProtoNumber(4)
            var count: Int = 100,
            @ProtoNumber(5)
            var labels: Labels = Labels()
        )

        @Serializable
        data class Labels(
            var nameHeader: String = "YOURS INITIALS",
            var scoreHeader: String = "HIGH SCORE",
            var scoreRank: String = "RANK",
            var scoreValue: String = "SCORE",
            var scoreName: String = "NAME",
        )

    }

    @Serializable
    data class Image(
        @ProtoNumber(1)
        override val appId: Long,
        @ProtoNumber(2)
        override val type: Int = AppType.IMAGE.type,
        @ProtoNumber(3)
        @ResClass(Content::class, false, false)
        var content: Content = Content()
    ) : App {
        @Serializable
        data class Content(
            @ProtoNumber(1)
            @ResImage(false)
            var id: Long = -1
        )
    }

    @Serializable
    data class Video(
        @ProtoNumber(1)
        override val appId: Long,
        @ProtoNumber(2)
        override val type: Int = AppType.VIDEO.type,
        @ProtoNumber(3)
        @ResClass(Content::class, false, false)
        var content: Content = Content()
    ) : App {
        @Serializable
        data class Content(
            @ProtoNumber(1)
            @ResVideo(false)
            var id: Long = -1,
            @ProtoNumber(4)
            var volume: Float = 0.5F,
            @ProtoNumber(5)
            var volumeMax: Float = 1F,
        )
    }

    @Serializable
    data class VideoAlphaLoop(
        @ProtoNumber(1)
        override val appId: Long,
        @ProtoNumber(2)
        override val type: Int = AppType.VIDEO_ALPHA_LOOP.type,
        @ProtoNumber(3)
        @ResClass(Content::class, false, false)
        var content: Content = Content()
    ) : App {
        @Serializable
        data class Content(
            @ProtoNumber(1)
            @ResVideoAlpha(false)
            var id: Long = -40,
            @ProtoNumber(2)
            var repeats: Int = 0,
            @ProtoNumber(3)
            var delayStart: Long = 0L,
            @ProtoNumber(4)
            var delayEnd: Long = 0L
        )
    }

    @Serializable
    data class UsbPlayer(
        @ProtoNumber(1)
        override val appId: Long,
        @ProtoNumber(2)
        override val type: Int = AppType.USB_PLAYER.type,
        @ProtoNumber(3) @ResClass(Content::class, false, false)
        var content: Content = Content()
    ) : App {
        @Serializable
        data class Content(
            @ProtoNumber(1) @ResWww(false)
            var id: Long = -41L,
            @ProtoNumber(2)
            var title: String = "Usb player",

            @ProtoNumber(3)
            var showVideo: Boolean = true,
            @ProtoNumber(4)
            var showImage: Boolean = true,

            @ProtoNumber(5)
            var showMediaName: Boolean = false,
            @ProtoNumber(6)
            var autoContinue: Boolean = true,

            @ProtoNumber(7)
            var sort: Int = Sort.NAME_ASC.mask,
            @ProtoNumber(8)
            var scale: Int = Scale.CENTER.mask,
        )

        enum class Sort(override val mask: Int) : Mask {
            NAME_ASC(2),
            NAME_DESC(4),
            DATE_ASC(8),
            DATE_DESC(16);

            companion object {
                operator fun get(mask: Int) = Mask[mask, Sort.entries.toList()]
            }
        }

        enum class Scale(override val mask: Int) : Mask {
            FILL(2),
            CENTER(4),
            CROP(8);

            companion object {
                operator fun get(mask: Int) = Mask[mask, Scale.entries.toList()]
            }
        }
    }

    @Serializable
    data class Maze(
        @ProtoNumber(1)
        override val appId: Long,
        @ProtoNumber(2)
        override val type: Int = AppType.MAZE.type,
        @ProtoNumber(3) @ResClass(Content::class, false, false)
        var content: Content = Content()
    ) : App {
        @Serializable
        data class Content(
            @ProtoNumber(1) @ResWww(false)
            var id: Long = -41L,
            @ProtoNumber(2)
            var title: String = "Maze",
            @ProtoNumber(3) @ResImage(false)
            var splashLand: Long = -42,
            @ProtoNumber(4) @ResImage(false)
            var splashPort: Long = -43,
            @ProtoNumber(5)
            var timer: GameTimer = GameTimer(
                game = 3.minutes.inWholeMilliseconds
            ),
            @ProtoNumber(6)
            var scale: Float = 1F,
        )
    }

    @Serializable
    data class Camera(
        @ProtoNumber(1)
        override val appId: Long,
        @ProtoNumber(2)
        override val type: Int = AppType.CAMERA.type,
    ) : App

    @Serializable
    data class Gallery(
        @ProtoNumber(1)
        override val appId: Long,
        @ProtoNumber(2)
        override val type: Int = AppType.GALLERY.type,
        @ProtoNumber(3)
        @ResClass(Content::class, false, false)
        var content: Content = Content()
    ) : App {
        @Serializable
        data class Content(
            @ProtoNumber(1)
            var volume: Float = 0.5F,
            @ProtoNumber(2)
            var volumeMax: Float = 1F,
            @ProtoNumber(3)
            @ResClass(Media::class, true, true)
            var media: MutableList<Media> = mutableListOf()
        )

        @Serializable
        sealed class Media {
            @ProtoNumber(1)
            abstract var id: Long

            @ProtoNumber(2)
            abstract var width: Int

            @ProtoNumber(3)
            abstract var height: Int

            @ProtoNumber(4)
            abstract var position: Int

            @Serializable
            data class Image(
                @ResImage(false)
                @ProtoNumber(1)
                override var id: Long,
                @ProtoNumber(2)
                override var width: Int,
                @ProtoNumber(3)
                override var height: Int,
                @ProtoNumber(4)
                override var position: Int
            ) : Media()

            @Serializable
            data class Video(
                @ResImage(false)
                @ResVideo(false)
                @ProtoNumber(1)
                override var id: Long,
                @ProtoNumber(2)
                override var width: Int,
                @ProtoNumber(3)
                override var height: Int,
                @ProtoNumber(4)
                override var position: Int,
                @ProtoNumber(5)
                var duration: Long
            ) : Media()
        }
    }

    @Serializable
    data class Pdf(
        @ProtoNumber(1)
        override val appId: Long,
        @ProtoNumber(2)
        override val type: Int = AppType.PDF.type,
        @ProtoNumber(3)
        @ResClass(Content::class, false, false)
        var content: Content = Content()
    ) : App {
        @Serializable
        data class Content(
            @ProtoNumber(1)
            @ResPdf(false)
            @ResImage(false)
            var id: Long = -1,
            var pagingMode: Int = PagingMode.SCROLL.mask
        )

        sealed class PagingMode(val mask: Int) {
            data object SCROLL : PagingMode(2)

            @Suppress("ClassName")
            data object PAGE_FLIP : PagingMode(4)

            infix fun match(mask: Int): Boolean = this.mask == mask

            companion object {
                val all by lazy {
                    listOf(SCROLL, PAGE_FLIP)
                }

                operator fun get(mask: Int): PagingMode {
                    return all.firstOrNull {
                        it.mask == mask
                    } ?: SCROLL
                }
            }
        }
    }

    @Serializable
    data class AppGroup(
        @ProtoNumber(1)
        override val appId: Long,
        @ProtoNumber(2)
        override val type: Int = AppType.APP_GROUP.type,
        @ProtoNumber(3)
        @ResClass(device.DeviceApps.Item::class, true, true)
        var apps: MutableList<device.DeviceApps.Item> = mutableListOf(),
        @ProtoNumber(4)
        var content: Content = Content()
    ) : App {
        @Serializable
        data class Content(
            @ProtoNumber(1)
            var labelSearch: String = "",
            @ProtoNumber(2)
            var showImages: Boolean = true,
        )

        fun allLabelsSet(): Boolean = with(content) {
            listOf(labelSearch).map { it.trim() }.none { it.isEmpty() }
        }
    }

    @Serializable
    data class WwwLink(
        @ProtoNumber(1)
        override val appId: Long,
        @ProtoNumber(2)
        override val type: Int = AppType.WWW_LINK.type,
        @ProtoNumber(3)
        var content: Content = Content()
    ) : App {
        @Serializable
        data class Content(
            @ProtoNumber(1)
            var url: String = "https://screensoft.org",
            @ProtoNumber(2)
            var keyboard: Boolean = false,
            @ProtoNumber(3)
            var blockOtherSites: Boolean = false,
            @ProtoNumber(4)
            var enabledSites: MutableList<EnabledSite> = mutableListOf(),
            @ProtoNumber(5)
            var zoomDefault: Int = 0,
            @ProtoNumber(6)
            var fullscreen: Boolean = false,
            @ProtoNumber(7)
            var controlsPanel: Boolean = true,
            /**
            @see App.WwwLink.TouchDragMode
             */
            @ProtoNumber(8)
            var touchDragMode: Int = TouchDragMode.SCROLL.mask,
            @ProtoNumber(9)
            var pageScale: Float = 1F
        )

        sealed class TouchDragMode(override val mask: Int) : Mask {
            data object SCROLL : TouchDragMode(2)
            data object DRAG : TouchDragMode(4)
        }

        @Serializable
        data class EnabledSite(
            @ProtoNumber(1)
            var id: Long = -1L,
            @ProtoNumber(2)
            var url: String = "https://screensoft.org",
        )
    }


    @Serializable
    data class WwwEmbedded(
        @ProtoNumber(1)
        override val appId: Long,
        @ProtoNumber(2)
        override val type: Int = AppType.WWW_EMBEDDED.type,
        @ProtoNumber(3)
        var content: Content = Content()
    ) : App {
        @Serializable
        data class Content(
            @ProtoNumber(1)
            var id: Long = -1L,
            @ProtoNumber(2)
            var name: String = "ScreenSoft.zip",
            @ProtoNumber(3)
            var keyboard: Boolean = false,
        )
    }

    @Serializable
    data class Rss(
        @ProtoNumber(1)
        override val appId: Long,
        @ProtoNumber(2)
        override val type: Int = AppType.RSS.type,
        @ProtoNumber(3)
        val content: Content = Content(),
    ) : App {

        @Serializable
        data class Content(
            @ProtoNumber(1)
            var channelId: Long = -1,
            @ProtoNumber(2)
            var labelSearch: String = "",
            @ProtoNumber(3)
            var labelCategory: String = "",
            @ProtoNumber(4)
            var labelItemDescription: String = "",
            @ProtoNumber(5)
            var labelItemCategory: String = "",
            @ProtoNumber(6)
            var labelItemAuthor: String = "",
            @ProtoNumber(7)
            var labelItemDetails: String = "",
            @ProtoNumber(8)
            var labelItemPublished: String = "",
            @ProtoNumber(9)
            var labelItemValidUntil: String = "",
            @ProtoNumber(10)
            var labelItemAttachments: String = "",
            @Deprecated(
                message = "use showDetail",
                replaceWith = ReplaceWith("showDetail")
            )
            @ProtoNumber(11)
            var channelMode: Int = 2,
            @ProtoNumber(12)
            var showDetail: Boolean = true,
            @ProtoNumber(13)
            var showListingDescription: Boolean = false,
            @ProtoNumber(14)
            var showListingImage: Boolean = true,
            @ProtoNumber(15)
            var showListingTitle: Boolean = true,
            @ProtoNumber(16)
            var showListingDescriptionAsHtml: Boolean = false,
            @ProtoNumber(17)
            var showItemDescriptionAsHtml: Boolean = false,
        )
    }

    @Serializable
    data class GameTimer(
        @ProtoNumber(1)
        var splash: Long = 10.seconds.inWholeMilliseconds,
        @ProtoNumber(2)
        var game: Long = 10.seconds.inWholeMilliseconds,
        @ProtoNumber(3)
        var result: Long = 10.seconds.inWholeMilliseconds,
    )

    @Serializable
    data class Wheel(
        @ProtoNumber(1)
        override val appId: Long,
        @ProtoNumber(2)
        override val type: Int = AppType.WHEEL.type,
        @ProtoNumber(3) @ResClass(Content::class, false, false)
        var content: Content = Content()
    ) : App {

        @Serializable
        data class Content(
            @ProtoNumber(1) @ResClass(Item::class, true, false)
            var items: MutableList<Item> = mutableListOf(
                Item.empty("item 1", 0, 30915300),
                Item.empty("item 2", 1, 22891300),
                Item.empty("item 3", 2, 25975300),
            ),
            @ProtoNumber(2) @ResClass(Theme::class, false, false)
            var theme: Theme = Theme(),
            @ProtoNumber(3) @ResClass(ConfigWheel::class, false, false)
            var wheel: ConfigWheel = ConfigWheel(),
            @ProtoNumber(6) @ResClass(ConfigBranding::class, false, false)
            var branding: ConfigBranding = ConfigBranding(),
            @ProtoNumber(4)
            var qr: ConfigQr = ConfigQr(),
            @ProtoNumber(5)
            var timer: GameTimer = GameTimer()
        )


        @Serializable
        data class Item(
            @ProtoNumber(1)
            var idGroup: Long = -1,
            @ProtoNumber(2)
            var idItem: Long = -1,
            @ProtoNumber(3)
            var name: String = "",
            @ProtoNumber(4)
            var count: Int = 100,
            @ProtoNumber(5) @ResClass(Icon::class, false, false)
            var icon: Icon = Icon(),
            @ProtoNumber(6)
            var color: Color = Color(),
            @ProtoNumber(7) @ResClass(Reward::class, false, false)
            var reward: Reward = Reward(),
            @ProtoNumber(8)
            var position: Int = 0,
            @ProtoNumber(9)
            var fontSize: Int = 60,
            @ProtoNumber(10)
            var nameY: Int = 30,
            @ProtoNumber(11)
            var used: Int = 0,
            @ProtoNumber(12)
            var countOrigin: Int = count,
        ) {
            companion object {
                fun empty(
                    title: String,
                    position: Int,
                    defaultSvg: Long = ResourceType.NO_ICON
                ): Item {
                    return (GenId.get() * -1L).let {
                        Item(
                            idItem = it,
                            idGroup = it,
                            name = title,
                            position = position,
                            icon = Icon(
                                svg = defaultSvg
                            )
                        )
                    }
                }
            }
        }

        @Serializable
        data class ConfigQr(
            @ProtoNumber(1)
            var scale: Int = 30,
            @ProtoNumber(2)
            var padding: Int = 10,
            @ProtoNumber(3)
            var x: Int = 80,
            @ProtoNumber(4)
            var y: Int = 90,
            @ProtoNumber(5)
            var visible: Boolean = true,
            @ProtoNumber(6)
            var touchable: Boolean = true,
        )

        @Serializable
        data class ConfigWheel(
            @ProtoNumber(1)
            var scale: Int = 90,
            @ProtoNumber(2)
            var x: Int = 50,
            @ProtoNumber(3)
            var y: Int = 50,
        )

        @Serializable
        data class ConfigBranding(
            @ProtoNumber(1)
            var showBranding: Boolean = true,
            @ProtoNumber(2) @ResImage(false, "data.showBranding && data.image>-999")
            var image: Long = -32L,
            @ProtoNumber(3)
            var scaleCircle: Int = 20,
            @ProtoNumber(4)
            var scaleImage: Int = 70,
            @ProtoNumber(5)
            var color: Color = Color(
                surface = 0xFFffFFff,
                onSurface = 0x00000000
            ),
        )

        @Serializable
        data class Theme(
            @ProtoNumber(1) @ResImage(false, "data.backgroundApp  != ResourceType.NO_IMAGE")
            var backgroundApp: Long = ResourceType.NO_IMAGE,
            @ProtoNumber(2) @ResImage(false)
            var backgroundStartup: Long = -37L,
            @ProtoNumber(3)
            var items: Color = Color(),
            /**
             * @see Theme.ORIENTATION_LAND
             * @see Theme.ORIENTATION_PORT
             */
            @ProtoNumber(4)
            var orientation: Int = ORIENTATION_PORT,
            @ProtoNumber(5)
            var itemsFontSize: Int = 80,
            @ProtoNumber(6)
            var iconsScale: Int = 50,
            @ProtoNumber(7)
            var iconsY: Int = 60,
            @ProtoNumber(8)
            var itemNameY: Int = 30,
            @ProtoNumber(9)
            var backgroundColor: Long = 0xFF47A8BD,
        ) {
            companion object {
                const val ORIENTATION_LAND = 0
                const val ORIENTATION_PORT = 1
            }

            val isLand: Boolean get() = orientation == ORIENTATION_LAND

        }

        @Serializable
        data class Reward(
            @ProtoNumber(1) @ResImage(false, "data.visual != ResourceType.NO_IMAGE")
            var visual: Long = ResourceType.NO_IMAGE
        )

        @Serializable
        data class Icon(
            @ProtoNumber(1) @ResImage(false, "data.webp  != ResourceType.NO_IMAGE")
            var webp: Long = ResourceType.NO_IMAGE,
            @ProtoNumber(2) @ResIcon(false, "data.svg  != ResourceType.NO_ICON")
            var svg: Long = ResourceType.NO_ICON,
            @ProtoNumber(3)
            var scale: Int = 45,
            @ProtoNumber(4)
            var y: Int = 60,
        )

    }

    @Serializable
    data class Color(
        @ProtoNumber(1)
        var surface: Long = 0xFF3f51b5,
        @ProtoNumber(2)
        var onSurface: Long = 0xFFffFFff,
    )

    @Serializable
    data class Kindergarten(
        @ProtoNumber(1)
        override val appId: Long,
        @ProtoNumber(2)
        override val type: Int = AppType.KINDERGARTEN.type,
        @ProtoNumber(3)
        @ResClass(Content::class, false, false)
        var content: Content = Content()
    ) : App {

        companion object {
            private val defaultTheme = ThemeType.Family
        }

        @Serializable
        data class Content(
            @ProtoNumber(1)
            @ResImage(false)
            var backgroundImg: Long = defaultTheme.imageBg,
            @ProtoNumber(2)
            var colors: Color = Color(
                surface = defaultTheme.defaultColor.surface,
                onSurface = defaultTheme.defaultColor.onSurface,
            ),
            /** @see ThemeType */
            @ProtoNumber(3)
            var themeType: Int = defaultTheme.mask,
            /** @see AudienceType */
            @ProtoNumber(4)
            var audienceType: Int = AudienceType.Small.mask,

            @ProtoNumber(5)
            @ResClass(Audio::class, true, false)
            var audio: MutableList<Audio> = mutableListOf(),

            @ProtoNumber(6)
            @ResRaw(false)
            var bundle: Long = defaultTheme.bundle,

            @ProtoNumber(7)
            @ResClass(ImagesCustom::class, false, false)
            var imagesCustom: ImagesCustom = ImagesCustom(),

            @ProtoNumber(8)
            var labels: Labels = Labels()
        )

        @Serializable
        class ImagesCustom(
            @ResClass(Photo::class, true, false)
            @ProtoNumber(1)
            var wireframe: MutableList<Photo> = mutableListOf(),
            @ResClass(Photo::class, true, false)
            @ProtoNumber(2)
            var photoLarge: MutableList<Photo> = mutableListOf(),
            @ResClass(Photo::class, true, false)
            @ProtoNumber(3)
            var photoSmall: MutableList<Photo> = mutableListOf(),
        )

        @Serializable
        class Photo(
            @ResImage(false)
            @ProtoNumber(1)
            var resource: Long = -1L,
            @ProtoNumber(2)
            var names: List<String> = emptyList(),
            @ProtoNumber(3)
            var colorBg: Long = 0x00000000,
        )

        @Serializable
        class Audio(
            @ResAudio(false)
            @ProtoNumber(1)
            var resource: Long = -1L,
            @ProtoNumber(2)
            var name: String = "",
            @ProtoNumber(3)
            var duration: Long = 0L
        )


        @Serializable
        class Labels(
            @ProtoNumber(1)
            var gameNameMemory: String = "",
            @ProtoNumber(2)
            var gameNameSudoku: String = "",
            @ProtoNumber(3)
            var gameNamePaint: String = "",
            @ProtoNumber(4)
            var gameNameSort: String = "",
            @ProtoNumber(5)
            var gameNameWords: String = "",
            @ProtoNumber(6)
            var gameNameMerge: String = "",
            @ProtoNumber(7)
            var gameNameOrder: String = "",
            @ProtoNumber(8)
            var gameNameCounts: String = "",
            @ProtoNumber(9)
            var gameNamePuzzle: String = "",
            @ProtoNumber(10)
            var alphabet: String = "A,B,C,D,E,F,G,H,I,J,K,L,M,N,O,P,Q,R,S,T,U,V,W,X,Y,Z"

        )

        sealed class AudienceType(
            override var mask: Int
        ) : Mask {
            data object Small : AudienceType(2)
            data object Large : AudienceType(4)

            companion object {

                val all by lazy {
                    listOf(
                        Small,
                        Large
                    )
                }

                operator fun get(mask: Int): AudienceType = Mask[mask, all] ?: Small

            }

            override fun toString(): String = mask.toString()

            override fun hashCode(): Int = mask

            override fun equals(other: Any?): Boolean {
                if (this === other) return true
                if (other == null || this::class != other::class) return false

                other as AudienceType

                return mask == other.mask
            }
        }

        sealed class ThemeType(
            override var mask: Int,
            val defaultColor: Color,
            val imageBg: Long,
            val bundle: Long,
        ) : Mask {

            data object Seasons : ThemeType(
                2,
                Color(
                    0xFF224477,
                    0xFFffFFff
                ),
                -1L, -1L,
            )

            data object AnimalsLocal : ThemeType(
                4,
                Color(
                    0xFF294A9A,
                    0xFFffFFff
                ),
                -47L, -47L
            )

            data object AnimalsExotic : ThemeType(
                8,
                Color(
                    0xFF294A9A,
                    0xFFffFFff
                ),
                -48L, -48L
            )

            data object Family : ThemeType(
                64,
                Color(
                    0xFF224477,
                    0xFFffFFff
                ),
                -49L, -49L
            )

            data object Transportation : ThemeType(
                16,
                Color(
                    0xFF224477,
                    0xFFffFFff
                ),
                -50L, -50L,
            )

            data object Holidays : ThemeType(
                32,
                Color(
                    0xFF224477,
                    0xFFffFFff
                ),
                -1L, -1L,
            )


            data object Food : ThemeType(
                128,
                Color(
                    0xFF224477,
                    0xFFffFFff
                ),
                -51L, -51L,
            )

            data object GeometricShapes : ThemeType(
                256,
                Color(
                    0xFF224477,
                    0xFFffFFff
                ),
                -1L, -1L,
            )


            companion object {

                val all by lazy {
                    listOf(
                        AnimalsLocal,
                        AnimalsExotic,
                        Transportation,
                        GeometricShapes,
                        Holidays,
                        Seasons,
                        Family,
                        Food,
                    )
                }

                operator fun get(mask: Int): ThemeType = Mask[mask, all] ?: Seasons

            }

            override fun toString(): String = mask.toString()

            override fun hashCode(): Int = mask

            override fun equals(other: Any?): Boolean {
                if (this === other) return true
                if (other == null || this::class != other::class) return false

                other as ThemeType

                return mask == other.mask
            }
        }
    }
}
